import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { CrmWaterPeriodsService } from '@services/crm-water-periods.service';

/**
 * The Pricing tab's gateway to the lake's season periods, and the one place that says when those
 * periods are missing.
 *
 * Seasons are the axis everything else on the tab is priced along: no season period means no grid
 * renders anywhere — not for a swim, not for an accommodation, not for whole-lake hire — and so nothing
 * can be priced at all. That state is stated here rather than reported as missing prices beside each
 * price list, because there are no prices to miss yet; the shortfall is a period that was never set up.
 * The same is true of the stay types, which are the lake's week/midweek/weekend flags on the General
 * tab, so that half is named too.
 *
 * Both come from the pricing-dimensions endpoint, which is what the grids themselves render their axes
 * from — so what this card warns about is exactly what they would refuse to show.
 */
@Component({
  selector: 'app-season-periods-card',
  templateUrl: './season-periods-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCardModule, MatButtonModule, MatIconModule, RouterLink, TranslatePipe],
})
export class SeasonPeriodsCardComponent {
  readonly waterId = input.required<number>();

  private readonly periodsService = inject(CrmWaterPeriodsService);

  private readonly dimensions = rxResource({
    params: () => ({ waterId: this.waterId() }),
    stream: ({ params }) => this.periodsService.getPricingDimensions(params.waterId),
  });

  /**
   * Only warn on a loaded answer. While the read is in flight — or after one that failed — the lake's
   * axes are unknown, and "no season periods configured" is far too strong a claim to make from silence.
   */
  private readonly loaded = computed(() => this.dimensions.value() !== undefined);

  readonly hasSeasons = computed(() => (this.dimensions.value()?.seasons.length ?? 0) > 0);
  readonly hasStayTypes = computed(() => (this.dimensions.value()?.stayTypes.length ?? 0) > 0);

  readonly showNoSeasonsWarning = computed(() => this.loaded() && !this.hasSeasons());
  readonly showNoStayTypesWarning = computed(() => this.loaded() && !this.hasStayTypes());
}
